import { FC, useId, useState } from "react"

import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import { ExportRowChevron } from "components/common/exportMenu"
import { useTranslation } from "react-i18next"
import { scrollbarStyles } from "themes/scrollbar"
import { IGroupScope, IGroupShort } from "ts/interfaces/Group"
import { groupScopeChipSx } from "utils/groupScope"

interface IProps {
  groups: IGroupShort[]
  // null = every group of the class
  selectedIds: number[] | null
  onChange: (ids: number[]) => void
}

const sections: {
  scope: IGroupScope
  labelKey: string
}[] = [
  {
    scope: "CLASS",
    labelKey: "group.filterSectionClass",
  },
  {
    scope: "SCHOOL",
    labelKey: "group.filterSectionSchool",
  },
]

const GroupsInReportPicker: FC<IProps> = ({
  groups,
  selectedIds,
  onChange,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const listId = useId()
  const [isListOpen, setIsListOpen] = useState(false)
  const allIds = groups.map((group) => group.id)
  const selected = selectedIds ?? allIds

  const toggleList = () => setIsListOpen((isOpen) => !isOpen)

  const toggleGroup = (id: number) =>
    onChange(
      selected.includes(id)
        ? selected.filter((el) => el !== id)
        : [...selected, id],
    )

  const toggleSection = (ids: number[], isAllSelected: boolean) =>
    onChange(
      isAllSelected
        ? selected.filter((id) => !ids.includes(id))
        : [...new Set([...selected, ...ids])],
    )

  return (
    <li>
      <Box
        sx={{
          bgcolor: "mockup.primary95",
          borderLeft: `3px solid ${theme.palette.primary.main}`,
          mt: 0.5,
        }}
      >
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={isListOpen}
          aria-controls={listId}
          onClick={toggleList}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault()
              toggleList()
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.25,
            cursor: "pointer",
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: -2,
            },
          }}
        >
          <Stack>
            <Typography variant="body3" color="mockup.neutral40">
              {t("group.exportStoreByGroupPicker")}
            </Typography>
            <Typography variant="body2" fontWeight="500">
              {selected.length === allIds.length
                ? t("group.exportStoreByGroupAll", { count: allIds.length })
                : t("group.exportStoreByGroupSome", {
                    selected: selected.length,
                    count: allIds.length,
                  })}
            </Typography>
          </Stack>
          <ExportRowChevron isOpen={isListOpen} />
        </Box>
        {isListOpen && (
          <Stack
            id={listId}
            gap={1.25}
            px={2}
            pb={1.5}
            maxHeight="220px"
            overflow="auto"
            sx={scrollbarStyles}
          >
            {sections.map(({ scope, labelKey }) => {
              const sectionGroups = groups.filter(
                (group) => group.scope === scope,
              )
              if (sectionGroups.length === 0) return null
              const ids = sectionGroups.map((group) => group.id)
              const isAllSelected = ids.every((id) => selected.includes(id))
              const isSomeSelected = ids.some((id) => selected.includes(id))

              return (
                <Stack key={scope} gap="2px">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Chip
                      size="small"
                      label={t(labelKey)}
                      sx={{ ...groupScopeChipSx[scope], fontWeight: 700 }}
                    />
                    <FormControlLabel
                      sx={{ ml: "auto", mr: 0 }}
                      labelPlacement="start"
                      control={
                        <Checkbox
                          size="small"
                          checked={isAllSelected}
                          indeterminate={isSomeSelected && !isAllSelected}
                          onChange={() => toggleSection(ids, isAllSelected)}
                        />
                      }
                      label={
                        <Typography variant="body3" color="mockup.neutral40">
                          {t("group.selectAll")}
                        </Typography>
                      }
                    />
                  </Stack>
                  {sectionGroups.map((group) => (
                    <FormControlLabel
                      key={group.id}
                      sx={{ ml: -0.5, mr: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={selected.includes(group.id)}
                          onChange={() => toggleGroup(group.id)}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          fontWeight="500"
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          {group.name}
                        </Typography>
                      }
                    />
                  ))}
                </Stack>
              )
            })}
          </Stack>
        )}
      </Box>
    </li>
  )
}

export default GroupsInReportPicker
